import { REDUCED_MOTION_QUERY } from "../hooks/usePrefersReducedMotion";
import { getAppliedTheme, resolveTheme, setThemePreference, ThemePreference } from "./designer-theme";
import "./theme-transition.scss";

/**
 * A theme switch the user asked for cross-fades the whole page: the browser snapshots the page in
 * the old theme, `apply` repaints it, and the old snapshot fades into the new theme (a View
 * Transition, timed in `theme-transition.scss`).
 *
 * The switch is instant — `apply` runs right away — where the browser has no View Transitions and
 * under reduced motion. An OS-driven change never comes through here: the user did not ask for it
 * on this page.
 */

/** Scopes the `::view-transition-*` rules to this transition, so any other one keeps the browser default. */
export const THEME_TRANSITION_CLASS: string = "kuika-theme-transition";

/**
 * The part of a View Transition this helper reads. lib.dom and react-router-dom both declare
 * `Document.startViewTransition`, returning differently shaped `ViewTransition`s; both carry this.
 */
interface IThemeViewTransition {
  finished: Promise<void>;
  /**
   * Rejects on its own when the browser refuses the transition — the page is hidden when the
   * switch lands, or a newer transition skips this one. Nothing here waits on it, so it is only
   * read to be silenced: an unhandled rejection would otherwise reach the console.
   */
  ready?: Promise<void>;
  /** Same treatment: `apply` is synchronous, so its promise is nothing this helper waits on. */
  updateCallbackDone?: Promise<void>;
}

const ignore = (): void => {};

/** The switch that owns the class; an earlier one finishing must not strip it. */
let activeTransition: IThemeViewTransition | null = null;

/**
 * While a transition runs, the browser hit-tests every press to `<html>` — the pseudo-element tree
 * covers the page, whatever its `pointer-events` — so a popup still open under it (the Appearance
 * menu itself) reads a second, quick pick as a click outside and closes. Those presses reach
 * nothing useful anyway; they are stopped before anything sees them.
 */
const PRESS_EVENTS: readonly string[] = ["pointerdown", "mousedown", "touchstart", "click"];

const swallowPressOnRoot = (event: Event): void => {
  if (event.target === document.documentElement) {
    event.stopPropagation();
  }
};

const setPressesSwallowed = (isSwallowed: boolean): void => {
  for (const type of PRESS_EVENTS) {
    if (isSwallowed) {
      window.addEventListener(type, swallowPressOnRoot, true);
    } else {
      window.removeEventListener(type, swallowPressOnRoot, true);
    }
  }
};

const prefersReducedMotion = (): boolean => {
  try {
    return typeof window.matchMedia === "function" && window.matchMedia(REDUCED_MOTION_QUERY).matches;
  } catch {
    return false;
  }
};

export const runThemeTransition = (apply: () => void): void => {
  if (typeof document.startViewTransition !== "function" || prefersReducedMotion()) {
    apply();
    return;
  }

  const root: HTMLElement = document.documentElement;
  root.classList.add(THEME_TRANSITION_CLASS);
  setPressesSwallowed(true);

  const transition: IThemeViewTransition = document.startViewTransition(apply);
  activeTransition = transition;

  const cleanUp = (): void => {
    if (activeTransition !== transition) {
      return;
    }
    activeTransition = null;
    setPressesSwallowed(false);
    root.classList.remove(THEME_TRANSITION_CLASS);
  };
  // `finished` rejects when a newer transition skips this one; either way it is over.
  transition.finished.then(cleanUp, cleanUp);
  transition.ready?.catch(ignore);
  transition.updateCallbackDone?.catch(ignore);
};

/**
 * Saves and paints `preference`, cross-fading to the new theme. A pick that paints the theme
 * already on screen (System on a dark OS while dark) has nothing to fade and just saves.
 */
export const changeThemePreference = (preference: ThemePreference): void => {
  const apply = (): void => setThemePreference(preference);

  if (resolveTheme(preference) === getAppliedTheme()) {
    apply();
    return;
  }

  runThemeTransition(apply);
};
